import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import NodeWrapper from "./NodeWrapper";
import useHandleConnected from "./useHandleConnected";
import { AltRoute } from "@mui/icons-material";

/**
 * Switch Node — Roteamento por valor (switch/case com N saidas).
 *
 * data.switchField: string — campo/variavel a avaliar
 * data.cases: [
 *   { value: string, label?: string }
 * ]
 * data.hasDefault: boolean — se tem saida default (fallback)
 *
 * Saidas: handle "case_0", "case_1", ..., "default"
 */
const CASE_COLORS = [
  "#1565C0", "#7B1FA2", "#00695C", "#E65100",
  "#AD1457", "#283593", "#1B5E20", "#4E342E",
];

const SwitchNode = memo(({ id, data, isConnectable, selected }) => {
  const getHandleClass = useHandleConnected(id);
  const field = data?.switchField || "variavel";
  const cases = data?.cases || [];
  const hasDefault = data?.hasDefault !== false;

  return (
    <NodeWrapper
      id={id}
      data={data}
      icon={<AltRoute />}
      title="Switch"
      color="#7B1FA2"
      bgColor="#F3E5F5"
      noSource
      isConnectable={isConnectable}
      selected={selected}
      minWidth={220}
    >
      {/* Field being evaluated */}
      <div style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 12,
        fontFamily: "'Fira Code', monospace",
        display: "flex",
        alignItems: "center",
        gap: 4,
        marginBottom: 6,
      }}>
        <span style={{ color: "#999", fontSize: 10 }}>switch</span>
        <span style={{ color: "#7B1FA2", fontWeight: 600 }}>${"{" + field + "}"}</span>
      </div>

      {/* Cases */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {cases.length === 0 ? (
          <div style={{ fontSize: 11, color: "#999", fontStyle: "italic" }}>
            Nenhum caso configurado
          </div>
        ) : (
          cases.slice(0, 6).map((c, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              gap: 6, position: "relative", paddingRight: 4,
            }}>
              <span style={{
                fontSize: 10, fontWeight: 600,
                color: "#fff",
                background: CASE_COLORS[i % CASE_COLORS.length],
                borderRadius: 4,
                padding: "2px 8px",
                maxWidth: 140,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {c.label || c.value || `Caso ${i + 1}`}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={`case_${i}`}
                className={getHandleClass("source", `case_${i}`)}
                style={{
                  position: "absolute", top: "50%", right: -13,
                  transform: "translateY(-50%)",
                  cursor: "crosshair",
                }}
                isConnectable={isConnectable}
              />
            </div>
          ))
        )}
        {cases.length > 6 && (
          <div style={{ fontSize: 10, color: "#999", textAlign: "right" }}>
            +{cases.length - 6} caso(s)
          </div>
        )}

        {/* Default */}
        {hasDefault && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "flex-end",
            gap: 6, position: "relative", paddingRight: 4,
            marginTop: 2,
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: "#fff",
              background: "#757575", borderRadius: 4,
              padding: "2px 8px", fontStyle: "italic",
            }}>
              Padrao
            </span>
            <Handle
              type="source"
              position={Position.Right}
              id="default"
              className={getHandleClass("source", "default")}
              style={{
                position: "absolute", top: "50%", right: -13,
                transform: "translateY(-50%)",
                cursor: "crosshair",
              }}
              isConnectable={isConnectable}
            />
          </div>
        )}
      </div>
    </NodeWrapper>
  );
});

SwitchNode.displayName = "SwitchNode";
export default SwitchNode;
